import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = ["width", "height"];
const _hoisted_2 = { class: "orb" };
const _hoisted_3 = ["fill"];
const _hoisted_4 = {
    key: 0,
    class: "wave"
};
const _hoisted_5 = ["x"];
const _hoisted_6 = {
    key: 1,
    class: "line",
    x: "20",
    y: "46",
    width: "24",
    height: "2"
};
import { ref, watch, onMounted, onBeforeUnmount, useId } from 'vue';
// The MG logo brought to life: idle breathes and blinks, thinking glances about, talking turns the line into a voice wave
export default /*@__PURE__*/ _defineComponent({
    __name: 'MgCharacter',
    props: {
        state: { default: 'idle' },
        size: { default: 28 }
    },
    setup(__props) {
        const props = __props;
        const gradientId = 'mg-orb-' + useId();
        // Idle mood: smiles, goes flat after 30s with no clicks or typing, yawns every 12s from 60s, frowns from 90s. Any activity brings the smile back.
        const mood = ref('happy');
        const yawning = ref(false);
        let moodTimers = [];
        let yawnInterval;
        function yawn() {
            yawning.value = true;
            moodTimers.push(setTimeout(() => { yawning.value = false; }, 2400));
        }
        // Hover: the orb shies away from the pointer (harder the closer it gets) and looks startled
        const svgEl = ref(null);
        const offset = ref(null);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        function dodge(e) {
            if (reducedMotion || !svgEl.value)
                return;
            const r = svgEl.value.getBoundingClientRect();
            // Measured from the orb's resting centre (32, 25.5 of 64) so the moving orb doesn't feed back into the maths
            const dx = 0.5 - (e.clientX - r.left) / r.width;
            const dy = 25.5 / 64 - (e.clientY - r.top) / r.height;
            const dist = Math.hypot(dx, dy);
            const push = 7 * (1 - Math.min(dist / 0.7, 1));
            offset.value = dist < 0.01 ? { x: 0, y: -push } : { x: (dx / dist) * push, y: (dy / dist) * push };
        }
        // Easter egg: every so often MG sticks its tongue out and winks
        const cheeky = ref(false);
        let cheekyInterval;
        function maybeBeCheeky() {
            if (props.state !== 'idle' || yawning.value || Math.random() > 0.08)
                return;
            cheeky.value = true;
            setTimeout(() => { cheeky.value = false; }, 1600);
        }
        function resetMood() {
            moodTimers.forEach(clearTimeout);
            clearInterval(yawnInterval);
            mood.value = 'happy';
            yawning.value = false;
            moodTimers = [
                setTimeout(() => { mood.value = 'flat'; }, 30000),
                setTimeout(() => {
                    yawn();
                    yawnInterval = setInterval(yawn, 12000);
                }, 60000),
                setTimeout(() => { mood.value = 'sad'; }, 90000),
            ];
        }
        watch(() => props.state, resetMood);
        onMounted(() => {
            resetMood();
            cheekyInterval = setInterval(maybeBeCheeky, 10000);
            window.addEventListener('keydown', resetMood);
            window.addEventListener('pointerdown', resetMood);
        });
        onBeforeUnmount(() => {
            moodTimers.forEach(clearTimeout);
            clearInterval(yawnInterval);
            clearInterval(cheekyInterval);
            window.removeEventListener('keydown', resetMood);
            window.removeEventListener('pointerdown', resetMood);
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("svg", {
                ref_key: "svgEl",
                ref: svgEl,
                class: _normalizeClass(["mg-character", [_ctx.state, { yawning: yawning.value, cheeky: cheeky.value, dodging: !!offset.value }]]),
                width: _ctx.size,
                height: _ctx.size,
                viewBox: "0 0 64 64",
                role: "img",
                "aria-label": "MG",
                onPointermove: dodge,
                onPointerleave: _cache[0] || (_cache[0] = ($event) => (offset.value = null))
            }, [
                _createElementVNode("defs", null, [
                    _createElementVNode("linearGradient", {
                        id: gradientId,
                        x1: "0",
                        y1: "0",
                        x2: "0",
                        y2: "1"
                    }, _cache[1] || (_cache[1] = [
                        _createElementVNode("stop", {
                            offset: "0",
                            class: "orb-light"
                        }, null, -1),
                        _createElementVNode("stop", {
                            offset: "1",
                            class: "orb-dark"
                        }, null, -1)
                    ]))
                ]),
                _cache[6] || (_cache[6] = _createElementVNode("circle", {
                    class: "disc",
                    cx: "32",
                    cy: "32",
                    r: "32"
                }, null, -1)),
                _createElementVNode("g", {
                    class: "dodge",
                    style: _normalizeStyle(offset.value ? `transform: translate(${offset.value.x}px, ${offset.value.y}px)` : '')
                }, [
                    _createElementVNode("g", _hoisted_2, [
                        _createElementVNode("circle", {
                            cx: "32",
                            cy: "25.5",
                            r: "12",
                            fill: `url(#${gradientId})`
                        }, null, 8, _hoisted_3),
                        _cache[2] || (_cache[2] = _createElementVNode("g", { class: "eyes" }, [
                            _createElementVNode("ellipse", {
                                cx: "27.5",
                                cy: "22.5",
                                rx: "1.5",
                                ry: "1.8"
                            }),
                            _createElementVNode("ellipse", {
                                class: "eye-right",
                                cx: "36.5",
                                cy: "22.5",
                                rx: "1.5",
                                ry: "1.8"
                            })
                        ], -1)),
                        _createElementVNode("path", {
                            class: _normalizeClass(["mouth", mood.value]),
                            d: "M28.5 28.5 Q32 31.5 35.5 28.5"
                        }, null, 2),
                        _cache[3] || (_cache[3] = _createElementVNode("ellipse", {
                            class: "yawn",
                            cx: "32",
                            cy: "29.5",
                            rx: "2.2",
                            ry: "2.8"
                        }, null, -1)),
                        _cache[4] || (_cache[4] = _createElementVNode("ellipse", {
                            class: "talk-mouth",
                            cx: "32",
                            cy: "29.2",
                            rx: "2",
                            ry: "1.5"
                        }, null, -1)),
                        _cache[5] || (_cache[5] = _createElementVNode("path", {
                            class: "tongue",
                            d: "M30.6 29.8 V31.4 A1.4 1.4 0 0 0 33.4 31.4 V29.8 M32 29.9 V31.2"
                        }, null, -1))
                    ])
                ], 4),
                (_ctx.state === 'talking')
                    ? (_openBlock(), _createElementBlock("g", _hoisted_4, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (i) => {
                            return _createElementVNode("rect", {
                                key: i,
                                x: 20 + (i - 1) * 5.2,
                                y: "46",
                                width: "3.2",
                                height: "2",
                                rx: "1"
                            }, null, 8, _hoisted_5);
                        }), 64))
                    ]))
                    : (_openBlock(), _createElementBlock("rect", _hoisted_6))
            ], 42, _hoisted_1));
        };
    }
});
